import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, X } from "lucide-react";
import { cn } from "@/lib/utils";
import { navLinks } from "@/data/siteConfig";
import { VelascoLogo } from "@/components/VelascoLogo";
import { CallButton } from "@/components/CallButton";

const testId = (path) => `nav-${path === "/" ? "home" : path.replace("/", "")}`;

export const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [pathname]);

  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [open]);

  return (
    <>
      <header
        className={cn(
          "fixed inset-x-0 top-0 z-50 transition-[background-color,border-color] duration-300",
          scrolled
            ? "border-b border-white/10 bg-ink/85 backdrop-blur-xl"
            : "border-b border-transparent bg-ink/30 backdrop-blur-md",
        )}
      >
        <nav className="mx-auto flex h-[72px] max-w-[1440px] items-center justify-between px-5 md:px-8">
          <Link to="/" data-testid="nav-logo" aria-label="Velasco Lock and Safe — Home">
            <VelascoLogo />
          </Link>

          <div className="hidden items-center gap-x-6 xl:flex">
            {navLinks
              .filter((l) => l.path !== "/")
              .map((link) => (
                <NavLink
                  key={link.path}
                  to={link.path}
                  data-testid={testId(link.path)}
                  className={({ isActive }) =>
                    cn(
                      "font-heading text-[13px] font-semibold uppercase tracking-wide transition-colors duration-200",
                      isActive ? "text-gold" : "text-steel-light hover:text-white",
                    )
                  }
                >
                  {link.label}
                </NavLink>
              ))}
          </div>

          <div className="flex items-center gap-3">
            <CallButton testid="nav-call-btn" className="hidden sm:inline-flex" />
            <button
              onClick={() => setOpen(true)}
              data-testid="mobile-menu-open"
              aria-label="Open navigation menu"
              className="flex h-11 w-11 items-center justify-center rounded-sm border border-white/15 text-white transition-colors duration-200 hover:bg-white/10 xl:hidden"
            >
              <Menu className="h-5 w-5" />
            </button>
          </div>
        </nav>
      </header>

      <AnimatePresence>
        {open && (
          <motion.div
            className="fixed inset-0 z-[60] xl:hidden"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.25 }}
            data-testid="mobile-menu"
          >
            <div className="absolute inset-0 bg-ink" />
            <div className="grain absolute inset-0" />
            <div className="relative flex h-full flex-col">
              <div className="flex h-[72px] items-center justify-between px-5">
                <VelascoLogo />
                <button
                  onClick={() => setOpen(false)}
                  data-testid="mobile-menu-close"
                  aria-label="Close navigation menu"
                  className="flex h-11 w-11 items-center justify-center rounded-sm border border-white/15 text-white transition-colors hover:bg-white/10"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>

              <div className="no-scrollbar flex flex-1 flex-col justify-center gap-0.5 overflow-y-auto px-6">
                {navLinks.map((link, i) => (
                  <motion.div
                    key={link.path}
                    initial={{ opacity: 0, x: -24 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.06 + i * 0.04, duration: 0.4 }}
                  >
                    <NavLink
                      to={link.path}
                      data-testid={`m-${testId(link.path)}`}
                      className={({ isActive }) =>
                        cn(
                          "flex items-baseline gap-4 border-b border-white/10 py-4 font-heading text-2xl font-bold uppercase tracking-tight transition-colors",
                          isActive ? "text-gold" : "text-white hover:text-gold",
                        )
                      }
                    >
                      <span className="text-xs font-semibold text-steel">
                        {String(i + 1).padStart(2, "0")}
                      </span>
                      {link.label}
                    </NavLink>
                  </motion.div>
                ))}
              </div>

              <div className="px-6 pb-8 pt-4">
                <CallButton size="lg" showNumber className="w-full" testid="mobile-menu-call" />
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};
